import { useEffect } from 'react'

import { useCameraValidationTest } from '../hooks/useCameraValidationTest'
import { useEditCamera } from '../hooks/useEditCamera'
import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { useLiveStreamSetup } from '@/hooks/useLiveStreamSetup'
import { usePermissions } from '@/hooks/usePermissions'
import { useToasts } from '@/hooks/useToasts'
import * as Sentry from '@sentry/nextjs'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  Box,
  Button,
  ColorScheme,
  Flex,
  Grid,
  GridItem,
  Stack,
} from '@hivewatch/dark'

import { ControlledSelect, ControlledTextInput } from '@/components/ui'

import { DarkEditDeviceSection } from '../../dark/DarkEditDeviceSection'
import { DarkEditDeviceSkeleton } from '../../dark/DarkEditDeviceSkeleton'
import { CameraFormDataTypeI } from '../../types/types'
import { isPlaced } from '../../utils/utils'
import { getCameraInputs } from '../utils/utils'
import { DarkCameraSecretProviderSection } from './DarkCameraSecretProviderSection'
import { DarkEditCameraLiveStreamingFields } from './DarkEditCameraLiveStreamingFields'
import { DarkValidateCameraStreamResults } from './DarkValidateCameraStreamResults'

interface DarkEditCameraFormIProps {
  colorScheme: ColorScheme
  deviceId: string
  shouldRequireAuthentication: boolean
  onRequiresAuthenticationChange: (isChecked: boolean) => void
  onLoadingChange: (loading: boolean) => void
}

export const DarkEditCameraForm = ({
  colorScheme,
  deviceId,
  shouldRequireAuthentication,
  onRequiresAuthenticationChange,
  onLoadingChange,
}: DarkEditCameraFormIProps) => {
  const { t } = useTranslation()
  const form = useForm<CameraFormDataTypeI>({ mode: 'onChange' })
  const hasCameraTestFeatureFlag = useFeatureFlag(FeatureFlag.cameraTestUtil)
  const {
    handleSubmit,
    errors,
    control,
    setValue,
    reset,
    formState: { dirtyFields, isDirty },
  } = form
  const { isOrangePeel } = useLiveStreamSetup()
  const { shouldEnableUpdateDevice, shouldShowCustomerSecretProviderSettings } =
    usePermissions()
  const {
    handleSetLiveStreamFields,
    shouldShowLiveStreamFields,
    cameraData,
    isLoading,
    floorsOptions,
    isUpdateDeviceLoading,
    isUpdateCameraLoading,
    updateCamera,
  } = useEditCamera(deviceId)
  const isSecretProviderLinked = Boolean(
    cameraData?.device?.camera?.customerSecretProvider?.node
  )
  const cameraValidationTest = useCameraValidationTest()
  const hasCameraTestResults = cameraValidationTest.finalResults !== undefined
  const { showSuccess, showError } = useToasts()

  const onSubmit: SubmitHandler<CameraFormDataTypeI> = async (values) => {
    const formData = {
      id: deviceId,
      name: values.name,
      floorId: values.location?.value,
      manufacturerName: values.manufacturerName,
    }
    const cameraInput = getCameraInputs(deviceId, values)

    try {
      await updateCamera(formData, cameraInput, dirtyFields)
      showSuccess(t('devices:crud_camera_update_success'))
      cameraValidationTest.reset()
      reset(values)
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          action: 'update',
          feature: 'edit_camera',
          surface: 'dark_edit_device_modal',
        },
        extra: { deviceId, dirtyFields: Object.keys(dirtyFields) },
      })
      showError(t('devices:crud_camera_update_error'))
    }
  }

  useEffect(() => {
    if (isDirty || isLoading) return

    setValue('name', cameraData?.device?.name, { shouldValidate: true })
    setValue(
      'location',
      floorsOptions.find(
        (option) => option.value === cameraData?.device?.floor?.id
      ),
      { shouldValidate: true }
    )
    setValue('externalId', cameraData?.device?.camera?.externalId)
    setValue('manufacturerName', cameraData?.device?.manufacturerName)
  }, [cameraData, floorsOptions, isDirty, isLoading, setValue])

  useEffect(() => {
    onLoadingChange(isLoading)
  }, [isLoading, onLoadingChange])

  useEffect(() => {
    if (!hasCameraTestResults) return

    const modalBody = document.getElementById('editCameraModalBody')
    if (modalBody) modalBody.scrollTop = modalBody.scrollHeight
  }, [hasCameraTestResults])

  if (!cameraData || isLoading) return <DarkEditDeviceSkeleton />

  const isSaveLoading = isUpdateDeviceLoading || isUpdateCameraLoading

  return (
    <Stack gap='16px'>
      <DarkEditDeviceSection
        actions={
          <Button
            colorScheme={colorScheme}
            disabled={!shouldEnableUpdateDevice || !isDirty}
            isLoading={isSaveLoading}
            onClick={handleSubmit(onSubmit)}
          >
            {isSaveLoading ? t('common:saving') : t('common:save')}
          </Button>
        }
        colorScheme={colorScheme}
        description={t('devices:crud_configure_description')}
        title={t('devices:crud_details_connection_title')}
      >
        <Grid
          gap='16px'
          templateColumns={{
            base: '1fr',
            md: 'repeat(2, 1fr)',
            xl: 'repeat(4, 1fr)',
          }}
        >
          <GridItem>
            <ControlledTextInput
              colorScheme={colorScheme}
              control={control}
              data-testid='devicesPage_editCameraModal_name'
              errors={errors}
              label={t('common:device_name')}
              name='name'
              placeholder={t('devices:crud_device_name_placeholder')}
              rules={{ required: t('devices:crud_device_name_required') }}
              width='100%'
            />
          </GridItem>
          <GridItem>
            <ControlledSelect
              colorScheme={colorScheme}
              control={control}
              data-testid='devicesPage_editCameraModal_location'
              hasWarning={Boolean(errors.location)}
              isClearable
              isDisabled={isPlaced(cameraData.device)}
              label={t('devices:crud_device_location_label')}
              name='location'
              options={floorsOptions}
              placeholder={t('devices:crud_device_location_select')}
              rules={{ required: true }}
              warningText={t('devices:crud_device_location_placeholder')}
            />
          </GridItem>
          <GridItem>
            <ControlledTextInput
              colorScheme={colorScheme}
              control={control}
              data-testid='devicesPage_editCameraModal_manufacturerName'
              errors={errors}
              label={t('devices:crud_device_manufacturer_label')}
              name='manufacturerName'
              placeholder={t('devices:crud_manufacturer_placeholder')}
              width='100%'
            />
          </GridItem>
          <GridItem>
            <ControlledTextInput
              colorScheme={colorScheme}
              control={control}
              data-testid='devicesPage_editCameraModal_externalId'
              errors={errors}
              label={t('devices:crud_external_id_label')}
              name='externalId'
              placeholder={t('devices:crud_external_id_placeholder')}
              width='100%'
            />
          </GridItem>
        </Grid>

        {shouldShowLiveStreamFields && (
          <Box mt='20px'>
            <DarkEditCameraLiveStreamingFields
              cameraData={cameraData}
              colorScheme={colorScheme}
              form={form}
              isLoading={isLoading}
              onRequiresAuthenticationChange={onRequiresAuthenticationChange}
              shouldRequireAuthentication={
                shouldRequireAuthentication && !isSecretProviderLinked
              }
            />
          </Box>
        )}

        {!shouldShowLiveStreamFields && isOrangePeel && (
          <Flex justify='center' mt='20px'>
            <Button
              colorScheme={colorScheme}
              data-testid='devicesPage_editCameraModal_liveStreamingButton'
              onClick={handleSetLiveStreamFields}
            >
              {t('devices:crud_live_streaming_label')}
            </Button>
          </Flex>
        )}

        {hasCameraTestFeatureFlag && hasCameraTestResults && (
          <Box mt='20px'>
            <DarkValidateCameraStreamResults
              colorScheme={colorScheme}
              deviceId={deviceId}
              errorMessages={cameraValidationTest.finalResults.errorMessages}
              url={cameraValidationTest.finalResults.url}
            />
          </Box>
        )}

        {hasCameraTestFeatureFlag && (
          <Flex mt='20px'>
            <Button
              colorScheme={colorScheme}
              disabled={isDirty}
              isLoading={cameraValidationTest.isTestingCamera}
              onClick={() =>
                cameraValidationTest.validateCameraStream(deviceId)
              }
              title={
                isDirty
                  ? t('devices:crud_test_save_first')
                  : t('devices:crud_test_camera_config')
              }
              variant='secondary'
            >
              {t('devices:crud_test_button')}
            </Button>
          </Flex>
        )}
      </DarkEditDeviceSection>

      {shouldShowCustomerSecretProviderSettings && (
        <DarkCameraSecretProviderSection
          cameraId={cameraData.device?.camera?.id}
          colorScheme={colorScheme}
          customerSecretProvider={
            cameraData.device?.camera?.customerSecretProvider
          }
        />
      )}
    </Stack>
  )
}
